import React from "react";
import { Box } from "@mui/material";
import RenderTextField from "../render-text-field";

/**
 * Interface defining the props for the ResetPasswordFields component.
 * @property {boolean} isNonMobile - Indicates if the viewport is non-mobile (desktop/tablet)
 * @property {any} values - Form field values
 * @property {any} errors - Form validation errors
 * @property {any} touched - Tracks which fields have been touched/interacted with
 * @property {Function} handleBlur - Handler for field blur events
 * @property {Function} handleChange - Handler for field change events
 */
type FormFieldsProps = {
  isNonMobile: boolean;
  values: any;
  errors: any;
  touched: any;
  handleBlur: any;
  handleChange: any;
};

/**
 * A form component for handling password reset functionality.
 * 
 * This component renders a responsive form with password and password confirmation fields,
 * including validation error handling and responsive layout adjustments.
 * 
 * @component
 * @param {FormFieldsProps} props - The props passed to the component
 * @param {boolean} props.isNonMobile - Controls responsive layout behavior
 * @param {any} props.values - Current form field values
 * @param {any} props.errors - Form validation errors
 * @param {any} props.touched - Tracks field interaction state
 * @param {Function} props.handleBlur - Handles field blur events
 * @param {Function} props.handleChange - Handles field change events
 * 
 * @example
 * ```tsx
 * <ResetPasswordFields
 *   isNonMobile={true}
 *   values={formValues}
 *   errors={formErrors}
 *   touched={touchedFields}
 *   handleBlur={handleBlur}
 *   handleChange={handleChange}
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI for layout and styling
 * - Implements responsive grid layout
 * - Includes form validation feedback
 * - Uses RenderTextField component for consistent field rendering
 * - Supports both mobile and desktop layouts
 * - Includes password confirmation field for security
 * - All fields are password type for secure input
 * 
 * @returns {JSX.Element} A React component that renders password reset form fields
 */
const ResetPasswordFields: React.FC<FormFieldsProps> = ({
  isNonMobile,
  values,
  errors,
  touched,
  handleBlur,
  handleChange,
}) => {
  return (
    <Box
      display="grid"
      gap="30px"
      gridTemplateColumns="repeat(4, minmax(0,1fr))"
      sx={{
        "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
      }}
    >
      <RenderTextField
        type="password"
        name="password"
        label="Neues Passwort"
        value={values.password}
        error={!!touched.password && !!errors.password}
        helperText={touched.password && errors.password}
        handleBlur={handleBlur}
        handleChange={handleChange}
        span="4"
      />
      <RenderTextField
        type="password"
        name="password_confirmation"
        label="Bestätige das Passwort"
        value={values.password_confirmation}
        error={
          !!touched.password_confirmation && !!errors.password_confirmation
        }
        helperText={
          touched.password_confirmation && errors.password_confirmation
        }
        handleBlur={handleBlur}
        handleChange={handleChange}
        span="4"
      />
    </Box>
  );
};

export default ResetPasswordFields;
